import React, { useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { UserContext, type UserContextValue } from '@core/context';
import useClassy from '@core/hooks/useClassy';
import useIsDryRunMode from '@core/hooks/useIsDryRunMode';
import useIsMigrationPreview from '@core/hooks/useIsMigrationPreview';
import { useSuperHubStore } from '@core/store';

import Button from '@ui/Button';
import Flex from '@ui/Flex';
import Icon from '@ui/Icon';
import Title from '@ui/Title';

import styles from './index.module.scss';

const urlRegex = /^https?:\/\//i;

type SectionType = 'changelog' | 'customPage' | 'discussion' | 'guide' | 'history' | 'recipe' | 'reference' | 'review';

export interface EmptyStateProps {
  /**
   * When provided, renders a CTA below the title and description as a button
   * link that navigates users to the absolute URL or relative path provided.
   */
  action?: string;
  className?: string;
  /**
   * When provided, renders a CTA whose clicks are handled by this callback.
   */
  onAction?: React.MouseEventHandler<HTMLAnchorElement | HTMLButtonElement>;
  /** Required field determines the content and copy to display to users. */
  section: SectionType;
}

/**
 * Renders an empty state for our hub sections. An optional CTA is rendered when
 * the `action` prop is provided with a truthy value. Use it to render a
 * clickable link or button that can then be handled with the `onAction` prop.
 * @example
 * ```tsx
 * <EmptyState section='changelog' action='/create/changelog' />
 * ```
 */
export default function EmptyState({ action, className, onAction, section }: EmptyStateProps) {
  const bem = useClassy(styles, 'EmptyState');
  const { isMigrationPreview } = useIsMigrationPreview();
  const user = useContext(UserContext) as unknown as UserContextValue;
  const { isGod } = user;
  const { isDryRunMode } = useIsDryRunMode();
  const { t } = useTranslation();
  const [isEditing] = useSuperHubStore(s => [s.isEditing]);

  /** Hash of empty state content based on section. */
  const template = useMemo(
    () =>
      ({
        changelog: {
          actionLabel: 'New Post',
          description: 'Try creating a new post!',
          icon: 'megaphone',
          title: isEditing ? 'No Changelogs' : t('emptyState.changelog.title'),
        },
        customPage: {
          actionLabel: 'New Page',
          description: 'Create your first custom page!',
          icon: 'custom-pages',
          title: 'No Custom Pages',
        },
        discussion: {
          actionLabel: t('emptyState.discussion.actionLabel'),
          description: t('emptyState.discussion.description'),
          icon: 'message-circle',
          title: t('emptyState.discussion.title'),
        },
        guide: {
          actionLabel: 'New Page',
          description: 'Create your first guides page!',
          icon: 'book-open',
          title: isEditing ? 'No Guides' : t('emptyState.guide.title'),
        },
        history: {
          actionLabel: '',
          description: '',
          icon: 'clock',
          title: 'No History',
        },
        recipe: {
          actionLabel: 'New Recipe',
          description: 'Recipes are task-driven guidance for your API. Try creating a new Recipe for your users!',
          icon: 'recipes',
          title: isEditing ? 'No Recipes' : t('emptyState.recipe.title'),
        },
        reference: {
          actionLabel: 'New API Definition',
          description: 'ReadMe can generate your API reference from a specification file.',
          icon: 'code',
          title: isEditing ? 'No API Endpoints' : t('emptyState.reference.title'),
        },
        review: {
          actionLabel: '',
          description: '',
          icon: 'badge-check',
          title: 'No Changes to Review',
        },
      }) satisfies Record<SectionType, { title: string; description: string; icon: string; actionLabel: string }>,
    [isEditing, t],
  );
  const { title, description, icon, actionLabel } = template[section];

  /** Only allow actionable state if not in preview mode or dry run mode, unless user is god. */
  const isActionableInPreview = (!isMigrationPreview && !isDryRunMode) || isGod;
  /** Whether this empty state should be actionable or not. */
  const isActionable = (!!action || !!onAction) && !!actionLabel && isActionableInPreview;
  const isAbsoluteUrl = !!action && urlRegex.test(action);
  const isRelativePath = !!action && !isAbsoluteUrl;

  return (
    <Flex align="center" className={bem('&', className)} justify="center">
      <Flex align="center" className={bem('-content')} gap="md" layout="col">
        <Icon color="blue" name={icon} size={40} />

        <Flex align="center" gap="xs" layout="col">
          <Title as={2} className={bem('-title')} level={3}>
            {title}
          </Title>
          {!!isActionable && <p className={bem('-description')}>{description}</p>}
        </Flex>

        {!!isActionable && (
          <Flex className={bem('-actions')} gap="sm">
            {section === 'recipe' && (
              <Button
                href="https://docs.readme.com/recipes/publish-all-docs-in-category"
                kind="secondary"
                size="sm"
                target="_blank"
              >
                Try Example
                <Icon name="arrow-up-right" />
              </Button>
            )}

            <Button
              href={isAbsoluteUrl ? action : undefined}
              kind="primary"
              onClick={onAction}
              size="sm"
              to={isRelativePath ? action : undefined}
            >
              <Icon name="plus" />
              {actionLabel}
            </Button>
          </Flex>
        )}
      </Flex>
    </Flex>
  );
}
